import type { CalcStep } from "@/lib/calc/steps";

/** Pure BMI helpers: adult categories (standard or WHO Asian cut-offs), healthy-range weight change, waist-to-height ratio, gauge geometry. */
export const STANDARD_CUTS = { under: 18.5, over: 25, obese: 30 } as const;
export const ASIAN_CUTS = { under: 18.5, over: 23, obese: 27.5 } as const;
export type Category = "Underweight" | "Normal" | "Overweight" | "Obese";

export const bmiOf = (kg: number, cm: number) => kg / ((cm / 100) ** 2);
export const roundBmi = (b: number) => Math.round(b * 10) / 10;
export const cutsFor = (asian: boolean) => (asian ? ASIAN_CUTS : STANDARD_CUTS);

/** Classifies the displayed (1 decimal) value, so what you see is what is classified. */
export function classify(shown: number, asian: boolean): Category {
  const c = cutsFor(asian);
  return shown < c.under ? "Underweight" : shown < c.over ? "Normal" : shown < c.obese ? "Overweight" : "Obese";
}

/** Upper end of the healthy range as a BMI value (24.9 standard, 22.9 Asian). */
export const healthyMaxBmi = (asian: boolean) => (asian ? 22.9 : 24.9);
export function healthyRangeKg(cm: number, asian: boolean) {
  const h2 = (cm / 100) ** 2;
  return { lo: 18.5 * h2, hi: healthyMaxBmi(asian) * h2 };
}

/** Kilograms to gain (positive) or lose (negative) to reach the healthy range. 0 when already normal. */
export function changeToHealthy(kg: number, cm: number, asian: boolean): number {
  const cat = classify(roundBmi(bmiOf(kg, cm)), asian);
  const { lo, hi } = healthyRangeKg(cm, asian);
  if (cat === "Underweight") return lo - kg;
  if (cat === "Overweight" || cat === "Obese") return hi - kg;
  return 0;
}

export type AgeStatus = { kind: "adult" } | { kind: "child"; message: string } | { kind: "invalid"; message: string } | { kind: "none" };
/** Age is optional. Under 20 the adult categories do not apply (CDC growth percentiles for ages 2-19; under 2 uses WHO length-for-weight). */
export function ageStatus(age: number | null, raw: string): AgeStatus {
  if (raw.trim() === "") return { kind: "none" };
  if (age == null || !Number.isFinite(age) || age < 0 || age > 120 || Math.floor(age) !== age) return { kind: "invalid", message: "Enter a whole age between 0 and 120, or leave it empty." };
  if (age < 2) return { kind: "child", message: "BMI is not used for children under 2. Ask your child's doctor to check growth with weight-for-length charts." };
  if (age < 20) return { kind: "child", message: "Adult BMI categories do not apply under age 20. For ages 2 to 19, BMI is read as a percentile for age and sex on the CDC growth charts. Ask your child's doctor or use the CDC child BMI percentile tool." };
  return { kind: "adult" };
}

export type WhtrBand = "low" | "below" | "above" | "high";
export function waistToHeight(waistCm: number, heightCm: number) {
  const ratio = waistCm / heightCm;
  const r = Math.round(ratio * 100) / 100;
  const band: WhtrBand = r < 0.4 ? "low" : r < 0.5 ? "below" : r < 0.6 ? "above" : "high";
  const note = band === "low" ? "Below 0.4. This is lower than typical; if you are unsure why, ask a doctor."
    : band === "below" ? "Below the 0.5 guideline (keep your waist under half your height)."
    : band === "above" ? "At or above the 0.5 guideline. A waist over half your height is linked with more health risk."
    : "0.6 or more, well above the 0.5 guideline. Consider talking to a doctor.";
  return { ratio: r, band, note, halfHeightCm: heightCm / 2 };
}

/** Gauge scale and segment geometry (fractions 0..1 of the bar). */
export const GAUGE_MIN = 15, GAUGE_MAX = 40;
export const gaugePos = (bmi: number) => Math.min(1, Math.max(0, (bmi - GAUGE_MIN) / (GAUGE_MAX - GAUGE_MIN)));
export function gaugeSegments(asian: boolean): { label: Category; from: number; to: number }[] {
  const c = cutsFor(asian);
  const f = (v: number) => gaugePos(v);
  return [
    { label: "Underweight", from: 0, to: f(c.under) }, { label: "Normal", from: f(c.under), to: f(c.over) },
    { label: "Overweight", from: f(c.over), to: f(c.obese) }, { label: "Obese", from: f(c.obese), to: 1 },
  ];
}

/* ---------------- B-0091: BMI Prime, Ponderal Index, WHO classes, target weight, working, share link ---------------- */

/** BMI Prime = BMI / 25, where 25 is the upper limit of the normal range. Below 0.74 is underweight, 0.74 to 1.0 normal, above 1.0 overweight. */
export const bmiPrime = (bmi: number) => Math.round((bmi / 25) * 100) / 100;
/** Ponderal Index = kg / m^3 (also called Rohrer's index), shown to one decimal. */
export const ponderalIndex = (kg: number, cm: number) => Math.round((kg / ((cm / 100) ** 3)) * 10) / 10;

export interface WhoClass { id: number; label: string; from: number; to: number | null; range: string }
/** The WHO adult BMI classification (WHO Technical Report Series 894, 2000; WHO Europe BMI table). Eight classes; ranges in kg/m2. */
export const WHO_CLASSES: WhoClass[] = [
  { id: 1, label: "Severe thinness", from: 0, to: 16, range: "below 16" },
  { id: 2, label: "Moderate thinness", from: 16, to: 17, range: "16 to 16.99" },
  { id: 3, label: "Mild thinness", from: 17, to: 18.5, range: "17 to 18.49" },
  { id: 4, label: "Normal range", from: 18.5, to: 25, range: "18.5 to 24.99" },
  { id: 5, label: "Pre-obese (overweight)", from: 25, to: 30, range: "25 to 29.99" },
  { id: 6, label: "Obese class I", from: 30, to: 35, range: "30 to 34.99" },
  { id: 7, label: "Obese class II", from: 35, to: 40, range: "35 to 39.99" },
  { id: 8, label: "Obese class III", from: 40, to: null, range: "40 and above" },
];
/** Classifies the displayed (1 decimal) BMI, like classify(). */
export function whoClass(shown: number): WhoClass {
  return WHO_CLASSES.find((c) => shown >= c.from && (c.to === null || shown < c.to)) ?? WHO_CLASSES[0];
}

export type TargetCheck = { ok: true; targetKg: number; deltaKg: number } | { ok: false; message: string };
/** Weight that gives a target BMI at this height, and the change from the current weight (negative = lose). */
export function targetWeight(kg: number, cm: number, targetBmi: number): TargetCheck {
  if (!(targetBmi >= 10 && targetBmi <= 60) || !Number.isFinite(targetBmi)) return { ok: false, message: "Enter a target BMI between 10 and 60." };
  const targetKg = targetBmi * ((cm / 100) ** 2);
  return { ok: true, targetKg, deltaKg: targetKg - kg };
}

export function bmiSteps(kg: number, cm: number, m: (kg: number) => string = (n) => `${n.toFixed(1)} kg`): CalcStep[] {
  const b = bmiOf(kg, cm), shown = roundBmi(b), who = whoClass(shown);
  return [
    { label: "Height in metres", formula: "height in cm / 100", inputs: { cm: +cm.toFixed(2) }, result: +(cm / 100).toFixed(4) },
    { label: "BMI", formula: "weight in kg / (height in m)^2", inputs: { weight: m(kg), height: `${+(cm / 100).toFixed(4)} m` }, result: shown.toFixed(1) },
    { label: "BMI Prime", formula: "BMI / 25", inputs: { BMI: shown.toFixed(1) }, result: bmiPrime(b).toFixed(2) },
    { label: "Ponderal Index", formula: "weight in kg / (height in m)^3", inputs: { weight: m(kg), height: `${+(cm / 100).toFixed(4)} m` }, result: `${ponderalIndex(kg, cm).toFixed(1)} kg/m3` },
    { label: "WHO class", formula: "the WHO adult BMI table row that contains the BMI", inputs: { BMI: shown.toFixed(1) }, result: `${who.label} (${who.range})` },
  ];
}

/** Share link: weight (kg), height (cm), units. Only in range numbers are accepted. */
export function bmiFromQuery(search: string): { weight: number | null; height: number | null; units: "metric" | "imperial" } {
  const p = new URLSearchParams(search);
  const num = (k: string, lo: number, hi: number) => { const v = p.get(k); if (v === null || !/^\d{1,3}(\.\d{1,2})?$/.test(v)) return null; const n = Number(v); return n >= lo && n <= hi ? n : null; };
  return { weight: num("kg", 30, 300), height: num("cm", 100, 250), units: p.get("u") === "imperial" ? "imperial" : "metric" };
}
export function bmiToQuery(kg: number | null, cm: number | null, units: "metric" | "imperial"): string {
  const p = new URLSearchParams();
  if (kg !== null && kg !== 70) p.set("kg", String(Math.round(kg * 100) / 100));
  if (cm !== null && cm !== 170) p.set("cm", String(Math.round(cm * 100) / 100));
  if (units === "imperial") p.set("u", "imperial");
  return p.toString();
}
